import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import PropTypes from 'prop-types';
import axios from 'axios';
import qs from 'qs';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../../envConfig';

const remoteService = RaaAuthenticatedRemoteService.globalInstance();

const selectBillingState = (state) => state.billing;

const selectServicePlan = createSelector(
  selectBillingState,
  (billing) => billing.servicePlan,
);

const selectBillingLoading = createSelector(
  selectBillingState,
  (billing) => billing.loading,
);

const selectContactInfoLoading = createSelector(
  selectBillingState,
  (billing) => billing.contactInfoLoading,
);

const selectBillingError = createSelector(
  selectBillingState,
  (billing) => billing.error,
);

const selectServiceAddressFieldErrors = createSelector(
  selectBillingState,
  (state) => state.serviceAddressForm.fieldErrors,
);

const selectServiceAddressFormError = createSelector(
  selectBillingState,
  (state) => state.serviceAddressForm.formError,
);

const selectBillingEmailFormError = createSelector(
  selectBillingState,
  (state) => state.billingEmailForm.formError,
);

const selectContactInfo = createSelector(
  selectBillingState,
  (state) => state.contactInfo,
);

const selectServiceAddress = createSelector(
  selectContactInfo,
  (contactInfo) => {
    if (!contactInfo || !contactInfo.serviceAddress) return null;
    return contactInfo.serviceAddress;
  },
);

const selectBillingEmail = createSelector(
  selectContactInfo,
  (contactInfo) => {
    if (!contactInfo || !contactInfo.billingEmail) return '';
    return contactInfo.billingEmail;
  },
);

const selectPaymentMethodFormError = createSelector(
  selectBillingState,
  (state) => state.paymentMethodForm.formError,
);

const selectPaymentMethodFieldErrors = createSelector(
  selectBillingState,
  (state) => state.paymentMethodForm.fieldErrors,
);

const selectPaymentInfo = createSelector(
  selectContactInfo,
  selectServicePlan,
  (contactInfo, servicePlan) => {
    if (!contactInfo) return null;

    const { cardOnFile } = contactInfo;

    const hasPaymentMethod = !!(
      cardOnFile && cardOnFile.cardNumber
    );

    return {
      hasPaymentMethod,
      amountDue:
            servicePlan && servicePlan.balance !== undefined
              ? servicePlan.balance
              : 0,
      dueDate:
            servicePlan && servicePlan.nextPaymentDue
              ? servicePlan.nextPaymentDue
              : null,
    };
  },
);

const selectTransactions = createSelector(
  selectBillingState,
  (state) => state.transactions,
);

const selectTransactionsLoading = createSelector(
  selectBillingState,
  (state) => state.transactionsLoading,
);

const selectTransactionsError = createSelector(
  selectBillingState,
  (state) => state.transactionsError,
);

const selectHasValidPromoEngagementDiscount = createSelector(
  selectServicePlan,
  (servicePlan) => !!(
    servicePlan
    && servicePlan.trial
    && !servicePlan.expired
    && servicePlan.promotionalEngagement
    && servicePlan.promotionalEngagement.claimedDate == null
  ),
);

const fetchServicePlan = createAsyncThunk(
  'securedApi/billing/fetchServicePlan',
  async (_, thunkAPI) => {
    try {
      const response = await remoteService.get(`${envConfig.apiUrl}/api/v1/billing/service-plan/current`);
      return response.servicePlan || null;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || null,
      });
    }
  },
);

const fetchContactInfo = createAsyncThunk(
  'securedApi/billing/fetchContactInfo',
  async (_, thunkAPI) => {
    try {
      return await remoteService.get(
        `${envConfig.apiUrl}/api/v1/billing/contact-info`,
      );
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || null,
      });
    }
  },
);

export const updateBillingEmail = createAsyncThunk(
  'securedApi/billing/updateBillingEmail',
  async (payload, thunkAPI) => {
    try {
      const response = await axios.post(
        `${envConfig.raaUrl}/subscription/billing/data/update-billing-email`,
        qs.stringify({
          billingEmail: payload.billingEmail,
        }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          },
          withCredentials: true,
        },
      );

      const result = response && response.data ? response.data : response;

      if (!result || result.success === false) {
        const message = result && result.message
          ? result.message
          : 'Unexpected error while updating billing email address.';

        return thunkAPI.rejectWithValue({
          formError: message,
        });
      }

      if (result && result.billingEmail) {
        return result.billingEmail;
      }

      if (payload && payload.billingEmail) {
        return payload.billingEmail;
      }

      return null;
    } catch (error) {
      let message = 'Unexpected error while updating billing email address.';

      if (error && error.response && error.response.data) {
        const { data } = error.response;

        if (data.message) {
          message = data.message;
        }
      }

      return thunkAPI.rejectWithValue({
        formError: message,
      });
    }
  },
);

export const deleteCreditCard = createAsyncThunk(
  'billing/deleteCreditCard',
  async ({ subscriptionId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${envConfig.raaUrl}/subscription/billing/data/delete-credit-card`,
        qs.stringify({ subscriptionId }),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          },
          withCredentials: true,
        },
      );

      return response.data;
    } catch (err) {
      return rejectWithValue(
        err.response.data || 'Unexpected error while removing credit card.',
      );
    }
  },
);

export const updateCreditCard = createAsyncThunk(
  'billing/updateCreditCard',
  async (payload, thunkAPI) => {
    try {
      const response = await axios.post(
        `${envConfig.raaUrl}/subscription/billing/data/update-card`,
        qs.stringify(payload),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          },
          withCredentials: true,
        },
      );
      const result = response && response.data ? response.data : response;
      if (!result || result.success === false) {
        const apiErrors = Array.isArray(result.data) ? result.data : [];

        const fieldErrors = {};
        const formErrors = [];

        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < apiErrors.length; i++) {
          const err = apiErrors[i];

          if (err && err.field) {
            fieldErrors[err.field] = err.message;
          } else if (err && err.message) {
            formErrors.push(err.message);
          }
        }

        return thunkAPI.rejectWithValue({
          fieldErrors,
          formError: formErrors.length
            ? formErrors.join(', ')
            : 'Validation failed',
        });
      }

      return payload;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        formError:
              error && error.message ? error.message : 'Network error',
      });
    }
  },
);

export const updateServiceAddress = createAsyncThunk(
  'securedApi/billing/updateServiceAddress',
  async (payload, thunkAPI) => {
    try {
      const response = await axios.post(
        `${envConfig.raaUrl}/subscription/billing/data/update-service-address`,
        qs.stringify(payload),
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          },
          withCredentials: true,
        },
      );

      const result = response && response.data ? response.data : response;

      if (!result || result.success === false) {
        const apiErrors = Array.isArray(result.data) ? result.data : [];

        const fieldErrors = {};
        const formErrors = [];

        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < apiErrors.length; i++) {
          const err = apiErrors[i];

          if (err && err.field) {
            fieldErrors[err.field] = err.message;
          } else if (err && err.message) {
            formErrors.push(err.message);
          }
        }

        return thunkAPI.rejectWithValue({
          fieldErrors,
          formError: formErrors.length
            ? formErrors.join(', ')
            : 'Validation failed',
        });
      }

      return payload;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        formError:
              error && error.message ? error.message : 'Network error',
      });
    }
  },
);

export const fetchTransactions = createAsyncThunk(
  'billing/fetchTransactions',
  async (_, thunkAPI) => {
    try {
      const response = await remoteService.get(
        `${envConfig.apiUrl}/api/v1/billing/service-plan/current/transaction-list`,
      );

      const result = response && response.data ? response.data : response;

      if (!result || !result.transactions) {
        return [];
      }

      return result.transactions;
    } catch (error) {
      let message = 'Failed to fetch transactions';

      if (error && error.response && error.response.data) {
        const { data } = error.response;
        if (data && data.message) {
          message = data.message;
        }
      }

      return thunkAPI.rejectWithValue({
        message,
      });
    }
  },
);

const initialState = {
  loading: true,
  contactInfoLoading: true,
  error: null,
  servicePlan: null,
  transactionsLoading: false,
  transactions: [],
  transactionsError: null,
  contactInfo: null,
  serviceAddressForm: {
    fieldErrors: {},
    formError: null,
  },
  billingEmailForm: {
    fieldErrors: {},
    formError: null,
  },
  paymentMethodForm: {
    fieldErrors: {},
    formError: null,
  },
};

export const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    clearBillingSlice: () => initialState,
    clearServiceAddressFormErrors: (state) => {
      state.serviceAddressForm.fieldErrors = {};
      state.serviceAddressForm.formError = null;
    },
    clearBillingEmailFormErrors: (state) => {
      state.billingEmailForm.formError = null;
    },
    clearPaymentMethodFormErrors: (state) => {
      state.paymentMethodForm.fieldErrors = {};
      state.paymentMethodForm.formError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServicePlan.pending, (state) => {
        state.loading = true;
        state.error = false;
      })
      .addCase(fetchServicePlan.fulfilled, (state, action) => {
        state.loading = false;
        state.servicePlan = action.payload;
      })
      .addCase(fetchServicePlan.rejected, (state) => {
        state.loading = false;
        state.error = true;
        state.servicePlan = null;
      })
      .addCase(fetchContactInfo.pending, (state) => {
        state.contactInfoLoading = true;
        state.error = null;
      })
      .addCase(fetchContactInfo.fulfilled, (state, action) => {
        state.contactInfoLoading = false;
        state.contactInfo = action.payload;
      })
      .addCase(fetchContactInfo.rejected, (state, action) => {
        state.contactInfoLoading = false;
        state.error = action.payload || true;
      })
      .addCase(updateCreditCard.pending, (state) => {
        state.paymentMethodForm.fieldErrors = {};
        state.paymentMethodForm.formError = null;
      })
      .addCase(updateCreditCard.fulfilled, (state) => {
        state.paymentMethodForm.fieldErrors = {};
        state.paymentMethodForm.formError = null;
        // state.contactInfo.paymentMethod = action.payload;
      })
      .addCase(updateCreditCard.rejected, (state, action) => {
        if (action.payload.fieldErrors) {
          state.paymentMethodForm.fieldErrors = action.payload.fieldErrors;
          state.paymentMethodForm.formError = action.payload.formError;
        } else {
          state.error = action.payload || { message: 'Something went wrong' };
        }
      })
      .addCase(updateServiceAddress.pending, (state) => {
        // state.loading = true;
        state.error = null;

        // clear previous form errors
        state.serviceAddressForm.fieldErrors = {};
        state.serviceAddressForm.formError = null;
      })
      .addCase(updateServiceAddress.fulfilled, (state, action) => {
        // state.loading = false;
        state.serviceAddress = action.payload;

        // clear form errors on success
        state.serviceAddressForm.fieldErrors = {};
        state.serviceAddressForm.formError = null;
      })
      .addCase(updateServiceAddress.rejected, (state, action) => {
        // state.loading = false;

        if (action.payload.fieldErrors) {
          // validation error case
          state.serviceAddressForm.fieldErrors = action.payload.fieldErrors;
          state.serviceAddressForm.formError = action.payload.formError;
        } else {
          // unexpected/system error
          state.error = action.payload || { message: 'Something went wrong' };
        }
      })
      .addCase(updateBillingEmail.pending, (state) => {
        state.billingEmailForm.formError = null;
      })
      .addCase(updateBillingEmail.fulfilled, (state, action) => {
        state.billingEmail = action.payload;
        state.billingEmailForm.formError = null;
      })
      .addCase(updateBillingEmail.rejected, (state, action) => {
        state.billingEmailForm.formError = action.payload && action.payload.formError
          ? action.payload.formError
          : 'Failed to update billing email';
      })
      .addCase(deleteCreditCard.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteCreditCard.fulfilled, (state) => {
        state.contactInfo.paymentMethod = null;
        state.error = null;
      })
      .addCase(deleteCreditCard.rejected, (state, action) => {
        state.error = action.payload || { message: 'Something went wrong' };
      })
      .addCase(fetchTransactions.pending, (state) => {
        state.transactionsLoading = true;
        state.transactionsError = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.transactionsLoading = false;
        state.transactions = action.payload;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.transactionsLoading = false;
        state.transactionsError = action.payload || { message: 'Something went wrong' };
      });
  },
});

const subscriptionTierPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  level: PropTypes.number.isRequired,
  isActive: PropTypes.bool.isRequired,
  isPublic: PropTypes.bool.isRequired,
  monthlyCrmProductId: PropTypes.number,
  yearlyCrmProductId: PropTypes.number,
  monthlyPrice: PropTypes.number.isRequired,
  yearlyPrice: PropTypes.number.isRequired,
  deviceLimit: PropTypes.number.isRequired,
  userLimit: PropTypes.number.isRequired,
  alertLimit: PropTypes.number.isRequired,
  dataAgeLimitUnit: PropTypes.string.isRequired,
  dataAgeLimitValue: PropTypes.number.isRequired,
  reportLimit: PropTypes.number.isRequired,
  csvExport: PropTypes.bool.isRequired,
  publicUrl: PropTypes.bool.isRequired,
  support: PropTypes.string.isRequired,
  hasAdvancedAlerting: PropTypes.bool.isRequired,
  hasCameraMonitoring: PropTypes.bool.isRequired,
  hasAdmPlugins: PropTypes.bool.isRequired,
  computedChannelLimit: PropTypes.number.isRequired,
  hasReportRanges: PropTypes.bool.isRequired,
  connectorLimit: PropTypes.number.isRequired,
  supportsRoles: PropTypes.bool.isRequired,
});

const servicePlanPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  subscriptionId: PropTypes.number,
  subscriptionTier: subscriptionTierPropType.isRequired,
  startTime: PropTypes.number.isRequired,
  endTime: PropTypes.number.isRequired,
  isCurrent: PropTypes.bool.isRequired,
  isExpired: PropTypes.bool.isRequired,
  initialAmount: PropTypes.string,
  initialPeriodUnit: PropTypes.string,
  initialPeriodValue: PropTypes.number,
  recurringAmount: PropTypes.string.isRequired,
  recurringInterval: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  orderNumber: PropTypes.string,
  productNumber: PropTypes.string,
  firstPaymentDueDate: PropTypes.number,
  renewalDate: PropTypes.number.isRequired,
  credit: PropTypes.number.isRequired,
  valueOfRemainingServiceCredit: PropTypes.number.isRequired,
});

// const serviceAddressPropType = PropTypes.shape({
//   id: PropTypes.number.isRequired,
//   subscriptionId: PropTypes.number,
//   name: PropTypes.string.isRequired,
//   streetLine1: PropTypes.string.isRequired,
//   streetLine2: PropTypes.string,
//   locality: PropTypes.string.isRequired,
//   state: PropTypes.string.isRequired,
//   postalCode: PropTypes.string.isRequired,
//   country: PropTypes.string.isRequired,
// });

const { actions, reducer } = billingSlice;
export const {
  clearBillingSlice,
  clearServiceAddressFormErrors,
  clearBillingEmailFormErrors,
  clearPaymentMethodFormErrors,
} = actions;

export {
  selectServicePlan,
  selectBillingLoading,
  selectBillingError,
  fetchServicePlan,
  fetchContactInfo,
  servicePlanPropType,
  subscriptionTierPropType,
  selectServiceAddressFormError,
  selectServiceAddressFieldErrors,
  selectBillingEmailFormError,
  selectContactInfo,
  selectServiceAddress,
  selectBillingEmail,
  selectPaymentInfo,
  selectContactInfoLoading,
  selectPaymentMethodFormError,
  selectPaymentMethodFieldErrors,
  selectTransactions,
  selectTransactionsLoading,
  selectTransactionsError,
  selectHasValidPromoEngagementDiscount,
};

export default reducer;
